import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  PLATFORM_ID,
  Self,
  SimpleChanges
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NgModel } from '@angular/forms';
import { Subscription } from 'rxjs';
import { showInputBubble } from '../shared/input-bubble';
import { guardPickerTimeFields, guardTimeTyping, interpretTime } from '../shared/time-input-rules';
import { formatClockTime } from '../utils/format';

const esLocale: any = {
  firstDayOfWeek: 1,
  weekdays: {
    shorthand: ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'],
    longhand: ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado']
  },
  months: {
    shorthand: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'],
    longhand: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']
  },
  ordinal: (nth: number) => (nth === 1 ? 'er' : 'º')
};

@Directive({
  selector: '[appDatePicker]',
  standalone: true
})
export class DateTimePickerDirective implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  @Input('appDatePicker') pickerType: 'date' | 'time' = 'date';
  @Input() minDate?: string;
  /** Solo fecha: último día elegible (YYYY-MM-DD). */
  @Input() maxDate?: string | null;
  /** Solo fecha: días que no se pueden elegir (YYYY-MM-DD) -> motivo (tooltip). */
  @Input() blockedDates?: Map<string, string> | null;
  @Input() disablePastDates = false;
  @Input() availableDays?: number[];
  @Input() minTime?: string;
  @Input() maxTime?: string;
  /** Solo hora: el valor debe ser POSTERIOR a esta (p. ej. fin > inicio, cierre > apertura). */
  @Input() afterTime?: string | null;
  /** Mensaje propio cuando no se cumple afterTime (por defecto: fin/inicio). */
  @Input() afterTimeMessage?: string;
  @Input() pickerDisabled = false;
  // Solo aplica a pickerType 'date'. Permite elegir varios días no
  // consecutivos en el mismo calendario (reservas multi-día). El modelo
  // (ngModel) sigue siendo un string — las fechas quedan unidas por coma,
  // igual que el modo de una sola fecha — para no romper ningún consumidor
  // existente del directive. `datesValueChange` además emite el arreglo ya
  // separado, por comodidad.
  @Input() multiple = false;
  // Opcional, solo pickerType 'date'. Minutos ocupados vs. minutos totales
  // de horario por fecha (YYYY-MM-DD) — quien use el directive decide si
  // tiene esos datos disponibles; sin este input el calendario simplemente
  // no dibuja el indicador de capacidad. Mismo mecanismo que
  // elhubsvoffice_front/src/app/directives/date-time-picker.directive.ts,
  // para que los calendarios de ambos frentes se vean y se comporten igual.
  @Input() dayOccupancy?: Map<string, { occupiedMinutes: number; totalMinutes: number }>;
  @Output() pickerValueChange = new EventEmitter<string>();
  @Output() datesValueChange = new EventEmitter<string[]>();

  private instance: any;
  private buildGeneration = 0;
  private modelSub?: Subscription;
  // Filtro de "desplazamiento real" — mismo fix que
  // elhubsvoffice_front/date-time-picker.directive.ts: sin esto, el
  // <select> nativo del mes (monthSelectorType:'dropdown') podía cerrar el
  // calendario en su primer clic si el navegador dispara ahí un scroll
  // "fantasma" (evento sin mover nada, mismo scrollY antes y después).
  // window/document usa una base capturada en caliente al abrir
  // (windowScrollBaseline) para que hasta el primer evento de un scroll
  // real ya cierre; un contenedor anidado usa su propio primer evento
  // como base (no se puede precapturar sin saber cuál será).
  private windowScrollBaseline = 0;
  private scrollBaseline = new WeakMap<EventTarget, number>();
  // El <select> nativo del mes abre un menú del sistema operativo que no
  // pertenece al DOM. Al enfocarlo/abrirlo, el navegador puede desplazar la
  // página (o el contenedor con scroll) para mostrarlo, y ese scroll llega
  // con target document/contenedor — no con target dentro del calendario,
  // así que el filtro de abajo no lo reconocía y cerraba el picker justo al
  // presionar un mes. Durante la interacción con ese selector se ignoran los
  // scrolls; la marca se refresca al abrir, al cambiar y al soltar el foco.
  private monthSelectorInteractionUntil = 0;
  private readonly closeOnScroll = (event: Event) => {
    const target = event.target as any;
    if (Date.now() < this.monthSelectorInteractionUntil) return;
    // Mientras el <select> del mes conserva el foco (menú nativo abierto o
    // recién elegido), cualquier scroll es consecuencia de esa interacción,
    // no del usuario desplazando la página: no debe cerrar el calendario.
    if (document.activeElement === this.instance?.monthsDropdownContainer) return;
    // El propio calendario también hace scroll interno (rejilla de meses,
    // selector nativo de mes). Un scroll originado dentro del picker no debe
    // cerrarlo: era lo que pasaba al desplegar/elegir el mes.
    if (target instanceof Node && (
      this.instance?.calendarContainer?.contains(target) ||
      this.element.nativeElement.contains(target)
    )) return;
    if (target === document || target === window) {
      const current = window.scrollY;
      const baseline = this.windowScrollBaseline;
      this.windowScrollBaseline = current;
      if (Math.abs(current - baseline) < 2) return;
    } else {
      const current = typeof target?.scrollTop === 'number' ? target.scrollTop : window.scrollY;
      const baseline = this.scrollBaseline.get(target);
      this.scrollBaseline.set(target, current);
      if (baseline === undefined || Math.abs(current - baseline) < 2) return;
    }
    try { this.instance?.close(); } catch { /* ignora */ }
  };
  private activeBubble: HTMLElement | null = null;
  private bubbleTimer: any;

  constructor(
    private readonly element: ElementRef<HTMLInputElement>,
    @Inject(PLATFORM_ID) private readonly platformId: object,
    @Optional() @Self() private readonly ngModel?: NgModel
  ) {}

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.initializePicker();
    // El input visible que flatpickr crea (altInput) no se entera de cambios
    // externos al modelo (p. ej. un botón "Restablecer" que limpia el valor);
    // solo reacciona a selecciones hechas dentro del propio calendario. Por eso
    // hay que empujarle el valor manualmente cuando el modelo cambia por fuera.
    if (this.ngModel) {
      this.modelSub = this.ngModel.valueChanges?.subscribe(value => {
        if (!this.instance) return;
        // Segundo argumento en false: no debe disparar el evento de cambio de
        // flatpickr, porque ese evento vuelve a escribir en el modelo y
        // dispararía este mismo listener de nuevo, entrando en bucle infinito.
        if (!value) this.instance.clear(false);
        else this.instance.setDate(value, false);
      });
    }
  }

  private afterMessage(after: string): string {
    const shown = formatClockTime(after);
    return this.afterTimeMessage ? `${this.afterTimeMessage} (${shown}).` : `La hora de fin debe ser posterior a la de inicio (${shown}).`;
  }

  /** Valor actual del campo (modelo de ngModel o, en formularios reactivos, el input). */
  private currentValue(): string {
    const model = this.ngModel?.value;
    return String((typeof model === 'string' ? model : this.element.nativeElement.value) || '').slice(0, 5);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Si cambia la hora de inicio y la de fin deja de ser posterior, se quita con aviso.
    if (changes['afterTime'] && !changes['afterTime'].firstChange && this.pickerType === 'time' && this.instance) {
      // Se revisa tras aplicar el resto de cambios: si la pantalla ya vació
      // la hora de fin, no hay nada que avisar.
      setTimeout(() => {
        const after = this.afterTime ? String(this.afterTime).slice(0, 5) : '';
        const current = this.currentValue();
        if (!this.instance || !after || !current || current > after) return;
        const input = this.element.nativeElement;
        try { this.instance.clear(false); } catch { /* ignora */ }
        input.value = '';
        input.dispatchEvent(new Event('input', { bubbles: true }));
        this.pickerValueChange.emit('');
        showInputBubble(this.instance.altInput || input, `Quitamos esta hora: ${this.afterMessage(after).charAt(0).toLowerCase()}${this.afterMessage(after).slice(1)}`, 'info');
      });
    }
    if (
      this.instance &&
      (changes['availableDays'] || changes['minTime'] || changes['maxTime'] ||
        changes['minDate'] || changes['maxDate'] || changes['disablePastDates'] || changes['pickerDisabled'] || changes['multiple'])
    ) {
      this.initializePicker();
      return;
    }
    // dayOccupancy suele llegar después (viene de una llamada al backend
    // aparte) — un redraw() alcanza para repintar los días visibles con el
    // dato nuevo, sin destruir/recrear el calendario ni perder el mes que
    // el usuario tenga abierto.
    if ((changes['dayOccupancy'] || changes['blockedDates']) && this.instance) {
      this.instance.redraw();
    }
  }

  // Formatea una fecha LOCAL como YYYY-MM-DD sin pasar por UTC — evita el
  // corrimiento de un día que produce toISOString() cerca de medianoche.
  private formatIsoDate(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  private initializePicker(): void {
    const generation = ++this.buildGeneration;
    const input = this.element.nativeElement;
    const isTime = this.pickerType === 'time';
    const isMultiple = !isTime && this.multiple;
    const placeholder = input.placeholder || (isTime ? 'Seleccionar hora' : isMultiple ? 'Seleccionar días' : 'Seleccionar fecha');
    // El valor real y confiable es el del modelo (ngModel.value) — NUNCA el
    // DOM. `availableDays`/`minTime`/`maxTime`/`pickerDisabled` llegan de
    // forma asíncrona (fetch del horario del espacio) y disparan un reinit
    // posterior al primero; ese reinit destruye la instancia anterior de
    // flatpickr, y `destroy()` borra `input.value` como parte de su
    // limpieza. Leer el DOM en ese punto (incluso "antes" de destruir, por
    // temporización de microtasks) es frágil; leer directamente el valor que
    // Angular ya tiene en el modelo no depende en absoluto del estado del
    // DOM ni de cuándo ocurre el destroy — es la causa real de que la fecha
    // "se perdiera" al abrir "Editar reservación".
    const modelValue = this.ngModel?.value;
    const preservedValue = (typeof modelValue === 'string' && modelValue) ? modelValue : input.value;

    import('flatpickr').then(module => {
      if (generation !== this.buildGeneration) return;
      const flatpickr = module.default || module;

      if (typeof this.instance?.destroy === 'function') this.instance.destroy();
      const config: any = {
        mode: isMultiple ? 'multiple' : 'single',
        conjunction: ',',
        enableTime: isTime,
        noCalendar: isTime,
        dateFormat: isTime ? 'H:i' : 'Y-m-d',
        // Dropdown real (<select>) para saltar directo a cualquier mes —
        // mismo criterio que elhubsvoffice_front/date-time-picker.directive.ts.
        monthSelectorType: 'dropdown',
        altInput: true,
        // La API y las comparaciones conservan HH:mm; únicamente la vista usa
        // el reloj de 12 horas habitual en El Salvador para coincidir con el panel.
        altFormat: isTime ? 'h:i K' : 'j \\d\\e F, Y',
        // Fecha: solo desde el calendario (con burbuja si intentan escribir).
        // Hora: también se puede escribir ("9", "9:30", "930", "2 pm"),
        // validada con las mismas reglas que el front (shared/time-input-rules).
        allowInput: isTime,
        // Selector con a. m./p. m.; el valor guardado es HH:MM (24 h).
        time_24hr: false,
        errorHandler: () => undefined,
        // El popup se ancla a <body> en vez de quedar dentro del formulario:
        // los modales de reserva tienen scroll propio (overflow-y:auto), y un
        // popup posicionado dentro de ese contenedor queda recortado o se
        // superpone de forma extraña con el texto de ayuda de abajo.
        appendTo: document.body,
        locale: esLocale,
        disableMobile: true,
        minuteIncrement: 30,
        clickOpens: !this.pickerDisabled,
        onReady: (_dates: Date[], _value: string, instance: any) => {
          if (instance.altInput) instance.altInput.disabled = this.pickerDisabled;
          this.capTimeInputDigits(instance);
          this.guardMonthSelectorScroll(instance);
          const alt: HTMLInputElement | undefined = instance.altInput;
          if (alt) {
            // El altInput lo crea flatpickr fuera de la compilación de Angular,
            // así que no lleva el atributo de encapsulación y las reglas con
            // ámbito de componente no lo alcanzan. La clase marcadora permite
            // estilarlo desde styles.css (ver selects con contenedor propio).
            alt.classList.add('app-datepicker-alt-input');
            if (alt.closest('.date-field, .date-field-ctrl, .global-statistics-filters')) {
              alt.classList.add('app-datepicker-alt-inline');
            }
          }
          if (alt && isTime) {
            alt.setAttribute('placeholder', alt.getAttribute('placeholder') || 'Ej. 9:00');
            guardTimeTyping(alt);
            alt.addEventListener('input', () => { alt.dataset['timeDirty'] = '1'; });
            guardPickerTimeFields(instance);
          } else if (alt && !alt.dataset['dateHelp']) {
            alt.dataset['dateHelp'] = '1';
            alt.addEventListener('keydown', (e: KeyboardEvent) => {
              if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
                showInputBubble(alt, isMultiple ? 'Elige los días en el calendario.' : 'Elige la fecha en el calendario.', 'info');
              }
            });
          }
        },
        // El popup vive en <body>, en "position: absolute" respecto al
        // campo. Cuando el campo está dentro de un contenedor con su
        // propio scroll (los modales de reserva, ver comentario de
        // appendTo arriba) o con "position: sticky", su posición en el
        // documento cambia de forma no trivial mientras se hace scroll, y
        // el popup puede terminar recalculando de qué lado abrir a mitad
        // de camino — se ve como si "saltara" o quedara desligado del
        // campo. Cerrarlo apenas empieza el scroll es más simple y
        // confiable que perseguir la posición del campo en cada evento.
        onOpen: () => this.attachScrollCloseListener(),
        onClose: () => {
          if (isTime) this.commitTypedTimeOnClose();
          this.detachScrollCloseListener();
          this.dismissBubble();
        },
        onChange: (dates: Date[], value: string) => {
          // Las casillas del panel se confirman en onClose. Flatpickr puede
          // emitir aquí un cambio diferido con la selección anterior.
          if (isTime && this.instance?.__appTimePickerDirty === true) return;
          if (isTime && this.instance?.altInput) {
            delete this.instance.altInput.dataset['timeDirty'];
            delete this.instance.altInput.dataset['userTyped'];
          }
          if (isMultiple) {
            const isoDates = dates.map(d => this.formatIsoDate(d));
            const normalized = isoDates.join(',');
            input.value = normalized;
            input.dispatchEvent(new Event('input', { bubbles: true }));
            this.pickerValueChange.emit(normalized);
            this.datesValueChange.emit(isoDates);
            return;
          }
          const normalized = isTime && dates[0]
            ? `${String(dates[0].getHours()).padStart(2, '0')}:${String(dates[0].getMinutes()).padStart(2, '0')}`
            : value;
          const after = isTime && this.afterTime ? String(this.afterTime).slice(0, 5) : '';
          if (after && normalized && normalized <= after) {
            showInputBubble(this.instance?.altInput || input, this.afterMessage(after));
            const previous = this.currentValue();
            setTimeout(() => {
              if (previous && previous > after) this.instance?.setDate(previous, false);
              else this.instance?.clear(false);
            });
            return;
          }
          input.value = normalized;
          input.dispatchEvent(new Event('input', { bubbles: true }));
          this.pickerValueChange.emit(normalized);
        }
      };

      // Flatpickr procesa algunas opciones mediante setters internos. No deben
      // enviarse con valor undefined porque intenta interpretarlas como fechas.
      if (this.minDate) config.minDate = this.minDate;
      if (!isTime && this.maxDate) config.maxDate = this.maxDate;
      if (isTime && this.minTime) config.minTime = this.minTime;
      if (isTime && this.maxTime) config.maxTime = this.maxTime;
      if (isTime) {
        // El selector abre en la hora de apertura y lo escrito a mano se
        // valida: inválido -> burbuja y se conserva la hora anterior.
        config.defaultHour = this.minTime ? Number(String(this.minTime).slice(0, 2)) : 8;
        config.defaultMinute = 0;
        config.parseDate = (text: string) => {
          const alt: HTMLInputElement | undefined = this.instance?.altInput;
          const typed = alt?.dataset['userTyped'] === '1';
          if (alt) delete alt.dataset['userTyped'];
          if (!typed) {
            // Valor puesto por el sistema: se acepta sin validar ni mostrar avisos.
            const quiet = interpretTime(text, { halfHourOnly: false });
            if ('error' in quiet) return undefined;
            const [qh, qm] = quiet.value.split(':').map(Number);
            const quietDate = new Date();
            quietDate.setHours(qh, qm, 0, 0);
            return quietDate;
          }
          const result = interpretTime(text, { min: this.minTime, max: this.maxTime, after: this.afterTime || undefined });
          if ('error' in result) {
            const message = this.afterTime && /posterior a la de inicio/.test(result.error) ? this.afterMessage(String(this.afterTime).slice(0, 5)) : result.error;
            showInputBubble(this.instance?.altInput || input, message);
            const previous = this.instance?.selectedDates?.[0];
            const shown = this.currentValue();
            if (this.instance?.altInput) setTimeout(() => { this.instance.altInput.value = shown; });
            return previous ? new Date(previous.getTime()) : undefined;
          }
          const [h, m] = result.value.split(':').map(Number);
          const date = new Date();
          date.setHours(h, m, 0, 0);
          return date;
        };
      }
      if (!isTime) {
        // Lee this.availableDays/this.dayOccupancy en cada llamada (no una
        // copia capturada al construir el config) para que un día que se
        // llena de reservas DESPUÉS de abierto el calendario (dayOccupancy
        // llega tarde) quede deshabilitado también, sin reconstruir todo el
        // picker — solo hace falta un redraw(). Mismo mecanismo que
        // elhubsvoffice_front/src/app/directives/date-time-picker.directive.ts.
        config.disable = [(date: Date) => {
          if (this.disablePastDates) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            if (date < today) return true;
          }
          if (this.availableDays && !new Set(this.availableDays).has(date.getDay())) return true;
          if (this.blockedDates?.has(this.formatIsoDate(date))) return true;
          const info = this.dayOccupancy?.get(this.formatIsoDate(date));
          return !!info && info.totalMinutes > 0 && info.occupiedMinutes >= info.totalMinutes;
        }];
      }
      if (!isTime) {
        config.onDayCreate = (_dates: Date[], _value: string, _instance: any, dayElem: HTMLElement) => this.decorateDay(dayElem);
      }

      this.instance = flatpickr(input, config);
      // NgModel aplica su valor en un microtask: si el reinicio lo disparó un
      // cambio de minDate/maxDate en el MISMO ciclo que cambió el valor (p. ej.
      // "Hasta" que se ajusta al mover "Desde"), preservedValue puede ser el
      // anterior -- ya fuera del nuevo rango -- y el campo quedaba vacío. Para
      // este punto (tras el import asíncrono) el modelo ya trae el valor vigente.
      const latestModel = this.ngModel?.value;
      const currentValue = (typeof latestModel === 'string' && latestModel) ? latestModel : (preservedValue || input.value);
      if (currentValue) {
        try { this.instance.setDate(currentValue, false); } catch { /* ignora */
        }
      }
    });
  }

  // Marca visualmente cada celda del calendario: una X sutil sobre los días
  // deshabilitados por horario (pasados o fuera de días de atención), un
  // ícono de candado sobre los días sin horario disponible por ya estar
  // completamente reservados (deshabilitados también, pero por una razón
  // distinta — el negocio SÍ atiende ese día, solo que ya no queda nada
  // libre) y, en los que aún admiten reserva pero ya tienen algo ocupado,
  // un relleno que cubre toda la celda, proporcional a cuánto del horario
  // del día ya está reservado (ver .app-day-occupancy en styles.css —
  // mismo diseño que elhubsvoffice_front). flatpickr ya deja `dateObj` y
  // la clase `flatpickr-disabled` puestos en el elemento antes de disparar
  // este hook.
  private decorateDay(dayElem: HTMLElement): void {
    dayElem.classList.remove('app-day-occupancy', 'app-day-disabled', 'app-day-full');
    dayElem.style.removeProperty('--app-day-occupancy-pct');
    dayElem.removeAttribute('title');
    dayElem.querySelector('.app-day-full-icon')?.remove();

    const date: Date | undefined = (dayElem as any).dateObj;
    const info = date ? this.dayOccupancy?.get(this.formatIsoDate(date)) : undefined;
    const isFull = !!info && info.totalMinutes > 0 && info.occupiedMinutes >= info.totalMinutes;

    if (dayElem.classList.contains('flatpickr-disabled') && !isFull) {
      dayElem.classList.add('app-day-disabled');
      const blockedReason = date ? this.blockedDates?.get(this.formatIsoDate(date)) : undefined;
      if (blockedReason) dayElem.title = blockedReason;
      return;
    }

    if (!info || info.occupiedMinutes <= 0 || info.totalMinutes <= 0) return;
    const pct = Math.max(4, Math.min(100, Math.round((info.occupiedMinutes / info.totalMinutes) * 100)));
    dayElem.classList.add('app-day-occupancy');
    dayElem.style.setProperty('--app-day-occupancy-pct', pct + '%');
    const occupiedH = Math.round(info.occupiedMinutes / 6) / 10;
    const totalH = Math.round(info.totalMinutes / 6) / 10;

    if (isFull) {
      // Además del relleno (que ya queda al 100%), un ícono propio deja
      // aún más claro que no es solo "casi lleno" sino que ya no queda
      // nada libre — y el clic, en vez de no hacer nada (day está
      // deshabilitado para flatpickr), muestra un mensaje flotante en
      // lugar de fallar en silencio.
      dayElem.classList.add('app-day-full');
      dayElem.title = 'Sin horas libres para reservas';
      const icon = document.createElement('span');
      icon.className = 'app-day-full-icon';
      icon.setAttribute('aria-hidden', 'true');
      icon.innerHTML = '<svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="11" width="16" height="9" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>';
      dayElem.appendChild(icon);
      dayElem.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.showFullyBookedBubble(dayElem);
      });
    } else {
      dayElem.title = `${occupiedH}h reservadas de ${totalH}h disponibles`;
    }
  }

  // "Estilo manga": una burbuja de diálogo flotante, no un simple
  // title/tooltip del navegador — aparece pegada al día presionado, con
  // colita apuntando hacia él, y se cierra sola. Vive en <body> (igual que
  // el propio calendario) para no quedar recortada por el overflow de
  // ningún contenedor intermedio.
  private showFullyBookedBubble(dayElem: HTMLElement): void {
    this.dismissBubble();
    const rect = dayElem.getBoundingClientRect();
    const bubble = document.createElement('div');
    bubble.className = 'app-day-bubble';
    bubble.textContent = 'Sin horas libres para reservas';
    document.body.appendChild(bubble);

    const bubbleRect = bubble.getBoundingClientRect();
    const spaceAbove = rect.top;
    const showBelow = spaceAbove < bubbleRect.height + 16;
    bubble.classList.toggle('below', showBelow);

    const left = rect.left + rect.width / 2 - bubbleRect.width / 2;
    const clampedLeft = Math.max(8, Math.min(left, window.innerWidth - bubbleRect.width - 8));
    const top = showBelow ? rect.bottom + 10 : rect.top - bubbleRect.height - 10;
    bubble.style.left = `${clampedLeft}px`;
    bubble.style.top = `${Math.max(8, top)}px`;
    // La colita apunta al centro del día, no al centro de la burbuja —
    // hace falta el desfase si el clamp de arriba la corrió hacia un lado.
    bubble.style.setProperty('--tail-offset', `${(rect.left + rect.width / 2) - clampedLeft}px`);

    requestAnimationFrame(() => bubble.classList.add('show'));
    this.activeBubble = bubble;
    this.bubbleTimer = setTimeout(() => this.dismissBubble(), 2600);
  }

  private dismissBubble(): void {
    if (this.bubbleTimer) clearTimeout(this.bubbleTimer);
    this.activeBubble?.remove();
    this.activeBubble = null;
  }

  /** Confirma al cerrar tanto el texto visible como las casillas del panel. */
  private commitTypedTimeOnClose(): void {
    const inst = this.instance;
    const alt: HTMLInputElement | undefined = inst?.altInput;
    if (!alt) return;
    const typedInField = alt.dataset['timeDirty'] === '1';
    const typedInPicker = inst.__appTimePickerDirty === true;
    if (!typedInField && !typedInPicker) return;

    delete alt.dataset['timeDirty'];
    delete alt.dataset['userTyped'];
    delete inst.__appTimePickerDirty;
    const pendingPickerTime = inst.__appPendingTime;
    delete inst.__appPendingTime;
    const pickerText = typedInPicker
      ? pendingPickerTime || `${inst.hourElement?.value || ''}:${inst.minuteElement?.value || '00'} ${inst.amPM?.textContent || ''}`
      : alt.value;
    const result = interpretTime(pickerText, { min: this.minTime, max: this.maxTime, after: this.afterTime || undefined });
    if ('error' in result) {
      const message = this.afterTime && /posterior a la de inicio/.test(result.error)
        ? this.afterMessage(String(this.afterTime).slice(0, 5))
        : result.error;
      showInputBubble(alt, message);
      const previous = this.currentValue();
      if (previous) inst.setDate(previous, false);
      else inst.clear(false);
      return;
    }

    inst.setDate(result.value, false);
    const input = this.element.nativeElement;
    input.value = result.value;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    this.pickerValueChange.emit(result.value);
  }

  // Flatpickr renderiza sus campos de hora/minuto como <input type="number">,
  // y el atributo HTML maxlength se ignora en inputs numéricos en todos los
  // navegadores — por eso, aunque flatpickr igual normaliza el valor final,
  // el usuario podía seguir tecleando dígitos de más antes de esa
  // normalización. Se recorta el valor en cada pulsación como en un input de
  // texto normal.
  private capTimeInputDigits(instance: any): void {
    const rememberPendingTime = () => {
      instance.__appTimePickerDirty = true;
      // El blur de Flatpickr puede restaurar la selección anterior antes de
      // onClose. Esta instantánea conserva exactamente la hora tecleada.
      instance.__appPendingTime = `${instance.hourElement?.value || ''}:${instance.minuteElement?.value || '00'} ${instance.amPM?.textContent || ''}`;
    };
    const cap = (el?: HTMLInputElement) => {
      if (!el) return;
      el.addEventListener('keydown', (event: KeyboardEvent) => {
        if (/^\d$/.test(event.key) || event.key === 'Backspace' || event.key === 'Delete') {
          instance.__appTimePickerDirty = true;
        }
      }, true);
      el.addEventListener('input', () => {
        if (el.value.length > 2) el.value = el.value.slice(0, 2);
        rememberPendingTime();
      }, true);
      el.addEventListener('blur', () => {
        if (instance.__appTimePickerDirty === true) rememberPendingTime();
      }, true);
    };
    cap(instance.hourElement);
    cap(instance.minuteElement);
    instance.amPM?.addEventListener('click', rememberPendingTime, true);
  }

  // El menú desplegable del mes lo dibuja el sistema operativo fuera del DOM:
  // al abrirlo, el navegador enfoca el <select> y puede hacer scroll para
  // dejarlo visible. Esa interacción no debe cerrar el calendario (era el
  // bug reportado al presionar un mes). El <select> vive dentro del picker,
  // pero el scroll resultante puede llegar con target document o con el
  // contenedor que se desplazó, por eso se marca por tiempo y no por target.
  private guardMonthSelectorScroll(instance: any): void {
    const select: HTMLSelectElement | undefined = instance?.monthsDropdownContainer;
    if (!select || select.dataset['appMonthScrollGuard'] === '1') return;
    select.dataset['appMonthScrollGuard'] = '1';
    const mark = () => { this.monthSelectorInteractionUntil = Date.now() + 2000; };
    select.addEventListener('mousedown', mark, true);
    select.addEventListener('touchstart', mark, { capture: true, passive: true });
    select.addEventListener('focusin', mark, true);
    select.addEventListener('change', mark, true);
    select.addEventListener('keydown', mark, true);
  }

  // capture:true porque el scroll de un contenedor con overflow propio (no
  // solo la ventana, p. ej. el cuerpo de un modal) no burbujea hasta
  // window — sí se puede escuchar en captura, así que esto cierra el
  // calendario sin importar cuál elemento sea el que efectivamente se
  // desplazó.
  private attachScrollCloseListener(): void {
    this.windowScrollBaseline = window.scrollY;
    this.scrollBaseline = new WeakMap<EventTarget, number>();
    window.addEventListener('scroll', this.closeOnScroll, { passive: true, capture: true });
  }
  private detachScrollCloseListener(): void {
    window.removeEventListener('scroll', this.closeOnScroll, { capture: true });
  }

  ngOnDestroy(): void {
    this.buildGeneration++;
    this.modelSub?.unsubscribe();
    this.detachScrollCloseListener();
    this.dismissBubble();
    if (typeof this.instance?.destroy === 'function') this.instance.destroy();
  }
}
